import SearchIcon from '@mui/icons-material/Search';
import { Stack, InputBase, Grid, Box } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { revokeClientUser, ClientContactShareDTO, revokeClientContact } from 'wisestep-api';
import { WsCheckboxList, WsButton } from 'wisestep-base';

import WsCheckBox from '../../../../components/WsCheckBox/WsCheckBox';
import Loader from '../../../../features/conversationWidget/components/Loader';
import {
  getActiveWorkspaceUsers,
  getClientContactActiveWorkspaceUsers,
  getUserNameByIdInWorkspaceUsers,
} from '../../../../features/users/helper/hooks';
import { warning, success } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';

import { StyledFooter } from './types';

export interface ICheckBoxListOptions {
  [key: string]: string;
}

const recruitersOptions: {
  [key: string]: string;
} = {};

export interface IAssignedUserListProps {
  clientId: string;
  clientContactId?: string;
  onAssign(selectedList?: string[], idNameMapping?: { email?: string; name?: string }): void;
  assignedUsersList?: string[];
  moduleName: string;
}

const AssignedClientUser = ({
  clientId,
  clientContactId,
  onAssign,
  assignedUsersList,
  moduleName,
}: IAssignedUserListProps) => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const methods = useForm();
  const { watch, setValue, getValues } = methods;
  const [assignedUsers, setAssignedUsers] = useState<string[] | undefined>(assignedUsersList);
  const [userIdToUsernameMap, setUserIdToUsernameMap] = useState<Record<string, string | null>>({});
  const [loadingUserIdToUsernameMap, setLoadingUserIdToUsernameMap] = useState<boolean>(true);
  const [loader, setLoader] = useState<boolean>(false);
  const [filteredRecruitersOptions, setFilteredRecruitersOptions] = useState<ICheckBoxListOptions>(
    {}
  );

  useEffect(() => {
    const getAssignedUsers = async () => {
      setLoader(true);
      let assigned: string[] = [];
      if (moduleName === 'CLIENT') {
        assigned = await getActiveWorkspaceUsers(clientId);
      } else {
        const clientShareDTO: ClientContactShareDTO = {
          clientId,
          clientContactId: clientContactId ?? '',
        };
        assigned = await getClientContactActiveWorkspaceUsers(clientShareDTO);
      }
      setAssignedUsers(assigned);
      setLoader(false);
      setLoadingUserIdToUsernameMap(true);
      setUserIdToUsernameMap(await getUserNameByIdInWorkspaceUsers(assigned));
      setLoadingUserIdToUsernameMap(false);
    };
    getAssignedUsers();
  }, [assignedUsersList, clientContactId, clientId, moduleName]);

  const handleSelectEvents = (checked: boolean) => {
    checked
      ? setValue('assignedUsersList', Object.keys(filteredRecruitersOptions))
      : setValue('assignedUsersList', []);
  };

  useEffect(() => {
    if (assignedUsers) {
      const options: ICheckBoxListOptions = {};
      assignedUsers.forEach((email) => {
        if (loadingUserIdToUsernameMap) {
          options[email] = 'Fetching Users...';
        } else {
          options[email] = `${userIdToUsernameMap[email]} (${email})`;
        }
      });
      setFilteredRecruitersOptions(options);
    }
  }, [assignedUsers, loadingUserIdToUsernameMap, userIdToUsernameMap]);

  const unAssign = async () => {
    try {
      let response;
      const selectedRecruiters = getValues('assignedUsersList') || [];
      if (moduleName == 'CLIENT') {
        const clientShareDTO = { clientId: clientId, userList: selectedRecruiters };
        response = await revokeClientUser(clientShareDTO);
      } else {
        const clientShareDTO: ClientContactShareDTO = {
          clientId: clientId,
          clientContactId: clientContactId ?? '',
          userList: selectedRecruiters,
        };
        response = await revokeClientContact(clientShareDTO);
      }
      if (response.status === 200) {
        setNotification(success('User unassigned Successfully'));
      }
      const updatedRecruitersOptions = { ...filteredRecruitersOptions };
      selectedRecruiters.forEach((user) => {
        delete updatedRecruitersOptions[user];
      });
      setFilteredRecruitersOptions(updatedRecruitersOptions);
      setValue('assignedUsersList', []);
      onAssign(JSON.parse(response?.config?.data)?.userList, userIdToUsernameMap);
    } catch (error) {
      setNotification(warning('Something Went Wrong , Please try again'));
    }
  };

  const objectFilter = (obj, predicate) => {
    return Object.keys(obj)
      .filter((key) => predicate(obj[key]))
      .reduce((res, key) => ((res[key] = obj[key]), res), {});
  };

  const recruitersInput = (event) => {
    if (!isEmpty(event.target.value)) {
      const filtered = objectFilter(recruitersOptions, (row) =>
        row.toLowerCase().includes(event.target.value.toLowerCase())
      );
      setFilteredRecruitersOptions(filtered);
    } else {
      setFilteredRecruitersOptions(filteredRecruitersOptions);
    }
  };

  const recruitersCommonList = () => (
    <>
      <Stack direction="row" spacing={1} alignItems={'center'}>
        <SearchIcon />
        <InputBase placeholder="Search User" onChange={recruitersInput} fullWidth />
      </Stack>
      <Stack spacing={1} sx={{ height: 'calc(100vh - 240px)', overflowY: 'auto' }}>
        <WsCheckBox
          name="selectAll"
          label="Select All Users"
          bold={true}
          onClick={handleSelectEvents}
        />
        <WsCheckboxList name="assignedUsersList" options={filteredRecruitersOptions} />
      </Stack>
    </>
  );

  return (
    <Box>
      <FormProvider {...methods}>
        {assignedUsers && assignedUsers.length > 0 && !loader ? (
          recruitersCommonList()
        ) : loader ? (
          <Box textAlign="center" pt={15} px={4}>
            <Loader />
          </Box>
        ) : (
          recruitersCommonList()
        )}
        <StyledFooter>
          <Grid container item justifyContent="space-between" py={1}>
            <WsButton variant="text" size="large">
              {`${watch('assignedUsersList')?.length ?? 0} Selected`}
            </WsButton>
            <WsButton variant="outlined" size="large" onClick={unAssign}>
              Unassign
            </WsButton>
          </Grid>
        </StyledFooter>
      </FormProvider>
    </Box>
  );
};

export default AssignedClientUser;
